/**
 * ICE Candidate Handler
 * Based on MobileCh3/src/lib/sip/index.ts:152-227
 * Provides smart ICE candidate prioritization with fast fallback
 */

import logger from '../services/logger';

export interface ICECandidateEvent {
  candidate?: {
    type?: string;
  };
  ready(): void;
}

export interface SessionTimings {
  gatheringStartAt?: number;
  checkingStartAt?: number;
  iceConnectedAt?: number;
  /** `accepted` event fired — SIP 200 OK sent/received, but media path not yet confirmed. */
  acceptedAt?: number;
  /** `confirmed` event fired — ACK exchanged, media path negotiated. */
  confirmedAt?: number;
  /** answerCall() entry to session.answer() call — measures UI/TURN wait on inbound answer path. */
  answerFlowStartAt?: number;
  /** Duration from answer tap to session.answer() for inbound calls. */
  answerSentMs?: number;
  dtlsConnectedAt?: number;
  firstRxAt?: number;
  pcListenerAttached?: boolean;
}

/**
 * Setup smart ICE candidate handling for a WebRTC session
 * Buffers ALL candidates and waits for optimal gathering before releasing
 * @param session - JsSIP RTCSession instance
 * @param timings - shared timings object populated by ice/dtls events
 */
export function setupICECandidateHandling(session: any, timings: SessionTimings = {}, hasTurn = false): void {
  // Smart ICE candidate prioritization with RFC 8838 compliant early release
  let candidateBuffer: ICECandidateEvent[] = [];
  let processingTimer: ReturnType<typeof setTimeout> | null = null;
  let earlyReleaseTimer: ReturnType<typeof setTimeout> | null = null;
  let hostCandidateFound = false;
  let hasReleasedEarly = false;
  let answerStartAt = 0; // set when peerconnection fires (= session.answer() called)
  // Per-candidate-type counts — emitted on gathering complete for parity with
  // mobile's `ice_gathering_complete` log so Kibana cross-client queries work.
  let hostCandidateCount = 0;
  let srflxCandidateCount = 0;
  let relayCandidateCount = 0;

  // JsSIP 3.10 calls ready() directly on the null/end-of-candidates RTCPeerConnection
  // event and on icegatheringstatechange='complete', both bypassing our buffering logic.
  // We register our own RTCPeerConnection listeners BEFORE JsSIP adds its (JsSIP emits
  // the 'peerconnection' event before calling _createLocalDescription() which sets up
  // its listeners), then use stopImmediatePropagation() to block premature SDP sending.
  //
  // IMPORTANT: For outgoing calls, JsSIP emits 'peerconnection' synchronously inside
  // call() before it returns — our session.on() listener would be registered too late.
  // In that case session._connection is already set, so we install directly on it.
  const installPeerConnectionHandlers = (peerconnection: RTCPeerConnection) => {
    answerStartAt = Date.now();

    const onIceCandidate = (event: RTCPeerConnectionIceEvent) => {
      if (!event.candidate) {
        event.stopImmediatePropagation();
        logger.debug('ICE null candidate intercepted — deferring to early-release timer', {
          source: 'sip',
          event: 'ice_null_intercepted',
        });
      }
    };

    const onIceGatheringStateChange = (event: Event) => {
      if ((event.target as RTCPeerConnection).iceGatheringState === 'complete') {
        event.stopImmediatePropagation();
      }
    };

    // JsSIP does not forward icecandidateerror to session listeners — attach directly.
    const onIceCandidateError = (event: Event) => {
      const e = event as RTCPeerConnectionIceErrorEvent;
      logger.warn('ICE candidate error', {
        source: 'sip',
        event: 'ice_candidate_error',
        errorCode: e.errorCode,
        errorText: e.errorText,
        url: e.url,
      });
    };

    peerconnection.addEventListener('icecandidate', onIceCandidate);
    peerconnection.addEventListener('icegatheringstatechange', onIceGatheringStateChange);
    peerconnection.addEventListener('icecandidateerror', onIceCandidateError);

    const cleanup = () => {
      peerconnection.removeEventListener('icecandidate', onIceCandidate);
      peerconnection.removeEventListener('icegatheringstatechange', onIceGatheringStateChange);
      peerconnection.removeEventListener('icecandidateerror', onIceCandidateError);
    };
    session.on('ended', cleanup);
    session.on('failed', cleanup);
  };

  if (session._connection) {
    // Outgoing call: peerconnection was created synchronously inside call() before
    // setupICECandidateHandling() was invoked — install handlers directly.
    installPeerConnectionHandlers(session._connection);
  } else {
    // Incoming call: peerconnection is created during answer() — wait for the event.
    session.on('peerconnection', ({ peerconnection }: { peerconnection: RTCPeerConnection }) => {
      installPeerConnectionHandlers(peerconnection);
    });
  }

  const flushBufferedCandidates = () => {
    if (!candidateBuffer.length) return;
    if (processingTimer) {
      clearTimeout(processingTimer);
      processingTimer = null;
    }

    // Sort candidates by priority: host > srflx > relay > unknown
    const sortedCandidates = candidateBuffer.sort((a, b) => {
      const typeA = a.candidate?.type || 'unknown';
      const typeB = b.candidate?.type || 'unknown';

      const priority: { [key: string]: number } = {
        host: 3,
        srflx: 2,
        relay: 1,
        unknown: 0,
      };
      return (priority[typeB] || 0) - (priority[typeA] || 0);
    });

    logger.debug(`Flushing ${sortedCandidates.length} buffered ICE candidates`, {
      source: 'sip',
      event: 'ice_candidates_flush',
      count: sortedCandidates.length,
    });

    sortedCandidates.forEach(candidateEvent => {
      candidateEvent.ready();
    });

    candidateBuffer = [];
  };

  const triggerEarlyRelease = (reason: string) => {
    if (hasReleasedEarly) return;
    hasReleasedEarly = true;

    if (earlyReleaseTimer) {
      clearTimeout(earlyReleaseTimer);
      earlyReleaseTimer = null;
    }

    const answerTo200OkMs = Date.now() - answerStartAt;
    logger.debug(`ICE early release: ${reason} (+${answerTo200OkMs}ms from answer)`, {
      source: 'sip',
      event: 'ice_early_release',
      reason,
      answer_to_200ok_ms: answerTo200OkMs,
    });
    flushBufferedCandidates();
  };

  session.on('icecandidate', function (e: ICECandidateEvent) {
    const candidate = e.candidate;
    const candidateType = candidate?.type || 'unknown';

    logger.debug(`ICE candidate received: ${candidateType}`, {
      source: 'sip',
      event: 'ice_candidate_received',
      candidateType,
    });

    // Buffer host candidates (don't send immediately - wait for STUN/TURN)
    if (candidateType === 'host') {
      hostCandidateFound = true;
      hostCandidateCount++;
      candidateBuffer.push(e);

      // Start early release timeout when first host candidate arrives.
      // 2s covers the case where STUN/TURN never responds — host-only ICE still works.
      // When srflx/relay arrive (normal case), they trigger release earlier via their handlers.
      if (!earlyReleaseTimer && !hasReleasedEarly) {
        earlyReleaseTimer = setTimeout(() => {
          triggerEarlyRelease('timeout - flushing all candidates (host + any srflx/relay)');
        }, 2000);
      }

      return;
    }

    // Track srflx candidate arrival
    if (candidateType === 'srflx') {
      srflxCandidateCount++;
      candidateBuffer.push(e);

      // Trigger early release when we have both host + srflx, but only if
      // TURN is not configured (relay candidates expected). When TURN is active,
      // wait for relay candidates so they're included in the initial SDP.
      if (hostCandidateFound && !hasReleasedEarly && !hasTurn) {
        triggerEarlyRelease('host + srflx available (optimal path)');
      } else if (hostCandidateFound && !hasReleasedEarly && hasTurn) {
        // TURN configured — relay may never arrive (slow/unreachable TURN).
        // Replace the 5s host-only timer with a 1s srflx-triggered timer so
        // the callee sends 200 OK ~1s after srflx instead of waiting 5s.
        if (earlyReleaseTimer) {
          clearTimeout(earlyReleaseTimer);
        }
        earlyReleaseTimer = setTimeout(() => {
          triggerEarlyRelease('host + srflx — relay timeout (TURN slow/unavailable)');
        }, 1000);
      }
      // Always return from srflx — when TURN is active we wait for relay,
      // never fall through to the 100ms batch timer.
      return;
    }

    // Track relay candidate arrival
    if (candidateType === 'relay') {
      relayCandidateCount++;
      candidateBuffer.push(e);

      // When TURN is active, release as soon as first relay candidate arrives
      if (hasTurn && hostCandidateFound && !hasReleasedEarly) {
        triggerEarlyRelease('host + relay available (TURN path)');
        return;
      }
    } else {
      // Buffer all other candidates (prflx, etc.)
      candidateBuffer.push(e);
    }

    // If gathering already released early, process remaining candidates immediately
    if (hasReleasedEarly) {
      e.ready();
      return;
    }

    // Batch processing for efficiency (RFC 8838)
    if (!processingTimer) {
      processingTimer = setTimeout(() => {
        flushBufferedCandidates();
      }, 100); // 100ms batching interval
    }
  });

  session.on('icecandidateerror', function (e: any) {
    logger.warn('ICE candidate error', { source: 'sip', event: 'ice_candidate_error', errorCode: e?.errorCode, url: e?.url });
  });

  // ---- ICE gathering state (measures total gathering duration) ----
  session.on('icegatheringstatechange', function () {
    try {
      const pc = session.connection as RTCPeerConnection | undefined;
      const state = pc?.iceGatheringState;
      const now = Date.now();

      if (state === 'gathering') {
        timings.gatheringStartAt = timings.gatheringStartAt ?? now;
      }

      logger.debug(`ICE gathering state: ${state}`, {
        source: 'sip',
        event: 'ice_gathering_state_change',
        gatheringState: state,
        durationMs: state === 'complete' && timings.gatheringStartAt ? now - timings.gatheringStartAt : null,
      });

      if (state === 'complete') {
        const gatherDurationMs = timings.gatheringStartAt ? now - timings.gatheringStartAt : null;
        logger.info(`ICE gathering complete — host:${hostCandidateCount} srflx:${srflxCandidateCount} relay:${relayCandidateCount}`, {
          source: 'sip',
          event: 'ice_gathering_complete',
          hostCount: hostCandidateCount,
          srflxCount: srflxCandidateCount,
          relayCount: relayCandidateCount,
          totalCandidates: hostCandidateCount + srflxCandidateCount + relayCandidateCount,
          hasHost: hostCandidateCount > 0,
          hasSrflx: srflxCandidateCount > 0,
          hasRelay: relayCandidateCount > 0,
          gatherDurationMs,
        });
      }
    } catch (err) {
      logger.warn('Error in icegatheringstatechange handler', { source: 'sip', event: 'ice_gathering_state_error' });
    }
  });

  // ---- ICE connection state (measures checking→connected, confirmed→connected) ----
  session.on('iceconnectionstatechange', function () {
    try {
      const pc = session.connection as RTCPeerConnection | undefined;
      const state = pc?.iceConnectionState;
      const now = Date.now();

      if (state === 'checking') {
        timings.checkingStartAt = timings.checkingStartAt ?? now;

        // Attach connectionstatechange once to measure DTLS handshake duration
        if (!timings.pcListenerAttached && pc) {
          timings.pcListenerAttached = true;
          pc.addEventListener('connectionstatechange', () => {
            const pcState = pc.connectionState;
            const pcNow = Date.now();

            logger.debug(`PeerConnection state: ${pcState}`, {
              source: 'sip',
              event: 'peer_connection_state_change',
              peerConnectionState: pcState,
              sinceIceConnectedMs: timings.iceConnectedAt ? pcNow - timings.iceConnectedAt : null,
              sinceConfirmedMs: timings.confirmedAt ? pcNow - timings.confirmedAt : null,
            });

            if (pcState === 'connected' && !timings.dtlsConnectedAt) {
              timings.dtlsConnectedAt = pcNow;
              logger.info('DTLS handshake complete — media path ready', {
                source: 'sip',
                event: 'dtls_connected',
                dtlsDurationMs: timings.iceConnectedAt ? pcNow - timings.iceConnectedAt : null,
                sinceCheckingMs: timings.checkingStartAt ? pcNow - timings.checkingStartAt : null,
                sinceConfirmedMs: timings.confirmedAt ? pcNow - timings.confirmedAt : null,
              });
            }
          });
        }
      }

      if ((state === 'connected' || state === 'completed') && !timings.iceConnectedAt) {
        timings.iceConnectedAt = now;
      }

      logger.info(`ICE connection state: ${state}`, {
        source: 'sip',
        event: 'ice_connection_state_change',
        iceState: state,
        sinceCheckingMs: state !== 'checking' && timings.checkingStartAt ? now - timings.checkingStartAt : null,
        sinceConfirmedMs: timings.confirmedAt ? now - timings.confirmedAt : null,
      });

      // ICE failure recovery: trigger ICE restart when the connection fails.
      // This catches ICE failures that aren't caused by network changes (e.g.
      // TURN allocation expiry, server-side connectivity loss, NAT rebinding).
      // The network change handler covers network-change-induced failures;
      // this covers everything else.
      if (state === 'failed' && session.isEstablished()) {
        if (session.__iceRestartInProgress) {
          logger.info('ICE failed but restart already in progress -- skipping', {
            source: 'sip',
            event: 'ice_failed_restart_in_progress',
          });
        } else {
          logger.warn('ICE connection failed -- triggering ICE restart re-INVITE', {
            source: 'sip',
            event: 'ice_failed_restart_triggered',
            sinceCheckingMs: timings.checkingStartAt ? now - timings.checkingStartAt : null,
            sinceConfirmedMs: timings.confirmedAt ? now - timings.confirmedAt : null,
          });

          session.__iceRestartInProgress = true;

          // Remove previous ICE-restart listeners to prevent accumulation
          // on repeated ICE failures.
          if (session.__iceRestartEndedListener) {
            session.removeListener('ended', session.__iceRestartEndedListener);
          }
          if (session.__iceRestartFailedListener) {
            session.removeListener('failed', session.__iceRestartFailedListener);
          }
          if (session.__iceRestartWatchdog) {
            clearTimeout(session.__iceRestartWatchdog);
          }

          const clearFlag = () => { session.__iceRestartInProgress = false; };
          const watchdog = setTimeout(clearFlag, 15000);
          session.__iceRestartWatchdog = watchdog;
          const clearWatchdog = () => { clearTimeout(watchdog); session.__iceRestartWatchdog = null; };
          const onEnded = () => { clearWatchdog(); clearFlag(); };
          const onFailed = () => { clearWatchdog(); clearFlag(); };
          session.__iceRestartEndedListener = onEnded;
          session.__iceRestartFailedListener = onFailed;
          session.once('ended', onEnded);
          session.once('failed', onFailed);

          try {
            session.renegotiate(
              { rtcOfferConstraints: { iceRestart: true } },
              () => {
                // done callback -- re-INVITE answered with 200 OK
                clearWatchdog();
                clearFlag();
              },
            );
          } catch (err) {
            clearWatchdog();
            clearFlag();
            logger.warn('ICE restart re-INVITE failed', {
              source: 'sip',
              event: 'ice_failed_restart_error',
              errorMessage: (err as Error)?.message,
            });
          }
        }
      }
    } catch (err) {
      logger.warn('Error in iceconnectionstatechange handler', { source: 'sip', event: 'ice_connection_state_error' });
    }
  });
}